import React, {FC, ReactNode, useEffect} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {ResolveDispatchProps, useAppDispatch} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../src/helpers/reselectHelpers';
import {
  makeGetContactsManagerParameters,
  makeGetContactsManagerSearchState,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {contactsManagerSearchTablesForceReloadedAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchActions';
import {closeSidePanelAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {
  contactsManagerFetchSearchContactsTableDataAsyncAction,
  contactsManagerSetUpContactsTableAction,
  contactsManagerTableAddSelectedCardIdsAction,
  contactsManagerTableRemoveSelectedCardIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {usePrevious} from '../../../../../../../../srcCommon/helpers/hookHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {makeGetDynamicColumns} from '../common/tableSelectors';
import {ContactsSearchTable} from './contactsSearchTable';

/*
 * Props.
 */

interface ContactsSearchTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  multiSelectHeaderActions: ReactNode;
  renderEmptyState: Renderer;
}
type ContactsSearchTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactsSearchTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ContactsSearchTableContainerComponentProps = ContactsSearchTableContainerPublicProps &
  ContactsSearchTableContainerStateProps &
  ContactsSearchTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetQuery() {
  return createSelector([makeGetContactsManagerParameters()], (params) => {
    if (params.type !== ContactsManagerParametersTypesEnum.SEARCH) {
      return '';
    }
    return params.query;
  });
}

/*
 * Component.
 */

export const ContactsSearchTableContainerComponent: FC<ContactsSearchTableContainerComponentProps> = (
  props,
) => {
  const {
    searchState,
    multiSelectHeaderActions,
    dynamicColumns,
    query,
    renderEmptyState,
    addSelectedCardIds,
    removeSelectedCardIds,
    closeSidePanel,
    onReset,
    onFetchAsync,
  } = props;

  const isInMultiSelectMode = Boolean(searchState?.contactsTableState?.isInMultiSelectMode);
  const {cardIds = [], selectedCardIds = []} = searchState?.contactsTableState ?? {};

  const previousIsInMultiSelectMode = usePrevious(isInMultiSelectMode);
  const dispatch = useAppDispatch();

  // When we change tabs, we should remove any selected card ids and close side panel.
  useInitialEffect(() => () => {
    removeSelectedCardIds(cardIds);
    closeSidePanel();
  });

  useEffect(() => {
    if (isInMultiSelectMode && isInMultiSelectMode !== previousIsInMultiSelectMode) {
      closeSidePanel();
    }
  }, [closeSidePanel, isInMultiSelectMode, previousIsInMultiSelectMode]);

  useEffect(() => {
    if (!searchState?.contactsTableState) {
      dispatch(contactsManagerSetUpContactsTableAction());
    }
  }, [dispatch, searchState?.contactsTableState]);

  return (
    <ContactsSearchTable
      cardIds={cardIds}
      multiSelectHeaderActions={multiSelectHeaderActions}
      dynamicColumns={dynamicColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      selectedCardIds={selectedCardIds}
      paginationData={searchState?.contactsTableState?.paginationData}
      query={query}
      onReset={onReset}
      onFetchAsync={onFetchAsync}
      renderEmptyState={renderEmptyState}
      addSelectedCardIds={addSelectedCardIds}
      removeSelectedCardIds={removeSelectedCardIds}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactsSearchTableContainerPublicProps>()(() => ({
  searchState: makeGetContactsManagerSearchState(),
  dynamicColumns: makeGetDynamicColumns(),
  query: makeGetQuery(),
}));

const mapDispatchToProps = makeMapDispatchToProps<ContactsSearchTableContainerPublicProps>()(({panelId}) => ({
  addSelectedCardIds: contactsManagerTableAddSelectedCardIdsAction,
  removeSelectedCardIds: contactsManagerTableRemoveSelectedCardIdsAction,
  closeSidePanel: closeSidePanelAction,
  onReset: () => contactsManagerSearchTablesForceReloadedAction(panelId),
  onFetchAsync: (query: string, cancelToken: CancelToken) =>
    contactsManagerFetchSearchContactsTableDataAsyncAction(panelId, query, cancelToken),
}));

export const ContactsSearchTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactsSearchTableContainerComponent);
